import React from 'react'
import { useTranslation } from 'react-i18next'
import DefaultLayout from '../components/layouts/DefaultLayout'
import {
  Box,
  Text,
  Container,
  Heading,
  VStack,
  HStack,
  Code,
  Divider,
  Badge,
  Card,
  CardBody,
  CardHeader,
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import { authState } from '../atoms/auth'

const Sample: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [auth] = useRecoilState(authState)

  return (
    <DefaultLayout>
      <Container maxW="container.xl" py={20}>
        <VStack spacing={8} align="stretch">
          <Box textAlign="center">
            <Heading
              as="h1"
              size="2xl"
              bgGradient="linear(to-r, brand.400, brand.600)"
              bgClip="text"
              fontWeight="bold"
              mb={4}
            >
              {t('샘플 페이지')}
            </Heading>
            <Text fontSize="lg" color="gray.600">
              {t('바이브코딩개발킷의 기능 예제와 사용법을 확인하세요')}
            </Text>
          </Box>

          <Card>
            <CardHeader>
              <Heading size="md">{t('인증 상태')}</Heading>
            </CardHeader>
            <CardBody>
              {auth.id ? (
                <VStack align="flex-start" spacing={2}>
                  <HStack>
                    <Badge colorScheme="green">{t('로그인됨')}</Badge>
                    {auth.isAdmin === 1 && (
                      <Badge colorScheme="brand">{t('관리자')}</Badge>
                    )}
                    <Text fontSize="sm" color="gray.600">
                      {t('사용자 ID')}: {auth.id}
                    </Text>
                  </HStack>
                  <Text fontSize="sm">
                    <strong>{t('아이디')}:</strong> {auth.account || '-'}
                  </Text>
                  <Text fontSize="sm">
                    <strong>{t('사용자명')}:</strong> {auth.username || '-'}
                  </Text>
                  {auth.isAdmin === 1 && (
                    <Text fontSize="sm">
                      <strong>{t('권한')}:</strong> {t('관리자')}
                    </Text>
                  )}
                </VStack>
              ) : (
                <HStack>
                  <Badge colorScheme="gray">{t('로그인 필요')}</Badge>
                  <Text fontSize="sm" color="gray.600">
                    {t('로그인하시면 사용자 정보를 확인할 수 있습니다.')}
                  </Text>
                </HStack>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <Heading size="md">{t('API 엔드포인트')}</Heading>
            </CardHeader>
            <CardBody>
              <VStack align="stretch" spacing={4}>
                <Box>
                  <Text fontWeight="bold" mb={2}>{t('회원가입')}</Text>
                  <Code colorScheme="brand" p={2} display="block" borderRadius="md">
                    POST /api/main/signUp
                  </Code>
                </Box>
                <Divider />
                <Box>
                  <Text fontWeight="bold" mb={2}>{t('로그인')}</Text>
                  <Code colorScheme="brand" p={2} display="block" borderRadius="md">
                    POST /api/main/signIn
                  </Code>
                </Box>
                <Divider />
                <Box>
                  <Text fontWeight="bold" mb={2}>{t('사용자 조회')}</Text>
                  <Code colorScheme="brand" p={2} display="block" borderRadius="md">
                    GET /api/main/getUser
                  </Code>
                </Box>
                <Divider />
                <Box>
                  <Text fontWeight="bold" mb={2}>{t('로그아웃')}</Text>
                  <Code colorScheme="brand" p={2} display="block" borderRadius="md">
                    POST /api/main/signOut
                  </Code>
                </Box>
                {auth.isAdmin === 1 && (
                  <>
                    <Divider />
                    <Box>
                      <Text fontWeight="bold" mb={2}>{t('사용자 목록 조회 (관리자)')}</Text>
                      <Code colorScheme="brand" p={2} display="block" borderRadius="md">
                        GET /api/admin/getUserList
                      </Code>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        Query: page, limit
                      </Text>
                    </Box>
                  </>
                )}
              </VStack>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <Heading size="md">{t('프로젝트 구조')}</Heading>
            </CardHeader>
            <CardBody>
              <VStack align="flex-start" spacing={3}>
                <Box>
                  <Text fontWeight="bold" mb={2}>Frontend</Text>
                  <Text fontSize="sm" color="gray.700">
                    React + TypeScript + Chakra UI 기반의 현대적인 웹 애플리케이션
                  </Text>
                </Box>
                <Divider />
                <Box>
                  <Text fontWeight="bold" mb={2}>Backend</Text>
                  <Text fontSize="sm" color="gray.700">
                    NestJS + TypeScript + TypeORM 기반의 RESTful API 서버
                  </Text>
                </Box>
                <Divider />
                <Box>
                  <Text fontWeight="bold" mb={2}>Database</Text>
                  <Text fontSize="sm" color="gray.700">
                    MySQL을 사용한 관계형 데이터베이스
                  </Text>
                </Box>
              </VStack>
            </CardBody>
          </Card>

          <Box
            p={6}
            bg="brand.50"
            borderRadius="lg"
            borderLeft="4px"
            borderColor="brand.500"
          >
            <Text fontWeight="bold" mb={2} color="brand.800">
              💡 {t('개발 팁')}
            </Text>
            <Text fontSize="sm" color="brand.700">
              {t('Swagger 문서는 Backend 서버 실행 후 http://localhost:9000/api에서 확인할 수 있습니다.')}
            </Text>
          </Box>
        </VStack>
      </Container>
    </DefaultLayout>
  )
}

export default Sample
